import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { isOnTurn, type HandSettlement, type HintSuggestion, type ReactionEvent, type ReactionId, type RoomInfo, type TableModel, type WireAction, type SpeechCue } from "@guandan/protocol";
import { GameSocket, type SocketStatus } from "../net/GameSocket.js";
import { EventScheduler, type Cue } from "./scheduler.js";

/**
 * Wires one game room to the UI: socket in, presented state out.
 *
 * The scheduler is the store — components read `presented` and never touch the
 * socket or the authoritative model directly.
 */

export interface Rejection {
  readonly code: string;
  /** Plain English, written by the server. Shown to the player verbatim. */
  readonly message: string;
  readonly at: number;
}

export type ActionState = "ready" | "waiting" | "presenting" | "reconnecting";

/** Room-scoped guard for the one cold-resync allowance attached to each turn. */
export class TurnRecoveryGate {
  readonly #claimed = new Set<string>();

  has(turnToken: string): boolean {
    return this.#claimed.has(turnToken);
  }

  claim(turnToken: string): boolean {
    if (this.#claimed.has(turnToken)) return false;
    this.#claimed.add(turnToken);
    return true;
  }
}

export function resolveActionState(
  status: SocketStatus,
  schedulerCanAct: boolean,
  presentedOnTurn: boolean,
  authoritativeOnTurn: boolean,
): ActionState {
  if (status !== "open") return "reconnecting";
  if (schedulerCanAct) return "ready";
  return presentedOnTurn || authoritativeOnTurn ? "presenting" : "waiting";
}

export interface GameSession {
  readonly presented: TableModel;
  readonly handSettlements: readonly HandSettlement[];
  readonly room: RoomInfo | null;
  readonly status: SocketStatus;
  /** True only when the queue has drained and it is this seat's turn. */
  readonly canAct: boolean;
  readonly actionState: ActionState;
  readonly lag: number;
  readonly rejection: Rejection | null;
  readonly hints: readonly HintSuggestion[];
  readonly hintPending: boolean;
  readonly serverNow: () => number;
  readonly act: (action: WireAction) => void;
  readonly requestHint: () => void;
  readonly clearHint: () => void;
  readonly sendReaction: (reactionId: ReactionId) => void;
  readonly dismissRejection: () => void;
}

export function useGameSession(
  roomId: string,
  onCue?: (cue: Cue) => void,
  onSpeechPrepare?: (cue: SpeechCue) => void,
  onReaction?: (reaction: ReactionEvent) => void,
): GameSession {
  const [handSettlements, setHandSettlements] = useState<readonly HandSettlement[]>([]);
  const [room, setRoom] = useState<RoomInfo | null>(null);
  const [status, setStatus] = useState<SocketStatus>("connecting");
  const statusRef = useRef<SocketStatus>("connecting");
  const [rejection, setRejection] = useState<Rejection | null>(null);
  const [hints, setHints] = useState<readonly HintSuggestion[]>([]);
  const [hintPending, setHintPending] = useState(false);
  const socketRef = useRef<GameSocket | null>(null);
  const cueRef = useRef(onCue);
  cueRef.current = onCue;
  const speechPrepareRef = useRef(onSpeechPrepare);
  speechPrepareRef.current = onSpeechPrepare;
  const reactionRef = useRef(onReaction);
  reactionRef.current = onReaction;

  const scheduler = useMemo(
    () => new EventScheduler(null, {
      onCue: (cue) => {
        if (cue.kind === "reset" || (statusRef.current === "open" && document.visibilityState !== "hidden")) cueRef.current?.(cue);
      },
      onPresentationEnd: () => cueRef.current?.({ kind: "clearPresentation" }),
      onSpeechPrepare: (cue) => speechPrepareRef.current?.(cue),
      now: () => socketRef.current?.serverNow() ?? Date.now(),
    }),
    [roomId],
  );

  useEffect(() => {
    if (import.meta.env.DEV && roomId === "presentation-qa") {
      let cancelled = false;
      let cleanup: (() => void) | undefined;
      void import("./presentationFixture.js").then(({ startPresentationFixture }) => {
        if (cancelled) return;
        statusRef.current = "open"; setStatus("open");
        cleanup = startPresentationFixture(scheduler, setRoom);
      });
      return () => { cancelled = true; statusRef.current = "closed"; cleanup?.(); };
    }
    // Reduced motion is a presentation concern — the table subscribes to it for
    // particles and CSS. The authoritative timeline is never adjusted for it.
    setHandSettlements([]);
    let recoveryTimer: number | null = null;
    const recoveryGate = new TurnRecoveryGate();
    let socket: GameSocket;
    const clearRecoveryTimer = () => {
      if (recoveryTimer !== null) window.clearTimeout(recoveryTimer);
      recoveryTimer = null;
    };
    const armRecovery = () => {
      clearRecoveryTimer();
      const authoritative = scheduler.getAuthoritative();
      if (!isOnTurn(authoritative) || recoveryGate.has(authoritative.turnToken)) return;
      const token = authoritative.turnToken;
      const delay = Math.max(0, authoritative.turnReadyAt - (socket?.serverNow() ?? Date.now())) + 1_000;
      recoveryTimer = window.setTimeout(() => {
        recoveryTimer = null;
        scheduler.wake();
        const current = scheduler.getAuthoritative();
        if (!socket.isOpen || !isOnTurn(current) || current.turnToken !== token || scheduler.canAct) return;
        // A cold snapshot is the same recovery a manual refresh used to give,
        // but bounded to once per turn so a real network outage cannot loop.
        if (recoveryGate.claim(token)) socket.resync(0);
      }, delay);
    };
    const wake = () => {
      if (document.visibilityState === "visible") scheduler.wake();
      else cueRef.current?.({ kind: "reset" });
      armRecovery();
    };

    socket = new GameSocket(roomId, {
      onStatus: (next) => {
        statusRef.current = next; setStatus(next);
        if (next === "open") wake();
        else cueRef.current?.({ kind: "reset" });
      },
      onMessage: (message) => {
        switch (message.kind) {
          case "hello":
            scheduler.setSuspended(message.room.lifecycle === "PAUSED" || message.room.lifecycle === "ABORTED");
            setRoom(message.room);
            if (message.snapshot.pendingSpeech !== undefined) scheduler.prepareSpeech(message.snapshot.pendingSpeech);
            scheduler.adopt(message.snapshot, message.seat);
            socket.markReceived(message.snapshot.seq);
            armRecovery();
            return;
          case "resume":
            scheduler.setSuspended(message.room.lifecycle === "PAUSED" || message.room.lifecycle === "ABORTED");
            setRoom(message.room);
            if (message.turn.pendingSpeech !== undefined) scheduler.prepareSpeech(message.turn.pendingSpeech);
            scheduler.resume(message.turn, message.seat, { from: message.from, to: message.to });
            socket.markReceived(message.from);
            armRecovery();
            return;
          case "settlements":
            setHandSettlements(message.hands);
            return;
          case "room":
            scheduler.setSuspended(message.room.lifecycle === "PAUSED" || message.room.lifecycle === "ABORTED");
            setRoom(message.room);
            return;
          case "events":
            scheduler.ingest(message.events);
            // Resume from what has *arrived*, not what has been shown: the
            // queue survives a reconnect, and asking again would duplicate it.
            for (const event of message.events) socket.markReceived(event.seq);
            armRecovery();
            return;
          case "speech":
            scheduler.ingestSpeech(message.voiceSeq, message.startAt, message.endAt, message.cue);
            return;
          case "hint":
            if (message.seq === scheduler.getPresented().seq) setHints(message.suggestions);
            setHintPending(false);
            return;
          case "reaction":
            reactionRef.current?.(message);
            return;
          case "rejected":
            setHintPending(false);
            if (message.code === "stale-seq" || message.code === "stale-turn") {
              // Not worth telling the player about — the table simply moved on.
              socket.resync(0);
              return;
            }
            setRejection({ code: message.code, message: message.message, at: Date.now() });
            return;
          default:
            return;
        }
      },
    });

    socketRef.current = socket;
    socket.connect();
    document.addEventListener("visibilitychange", wake);
    window.addEventListener("pageshow", wake);

    // Dev-only handle for inspecting a stuck table from the console.
    if (import.meta.env.DEV) {
      (window as unknown as { __gd?: unknown }).__gd = {
        scheduler, socket,
        state: () => ({
          presentedSeq: scheduler.getPresented().seq,
          authoritativeSeq: scheduler.getAuthoritative().seq,
          lag: scheduler.lag,
          canAct: scheduler.canAct,
          phase: scheduler.getPresented().phase,
          ...scheduler.debug,
        }),
      };
    }

    return () => {
      clearRecoveryTimer();
      document.removeEventListener("visibilitychange", wake);
      window.removeEventListener("pageshow", wake);
      socket.close();
      scheduler.detach();
      socketRef.current = null;
    };
  }, [roomId, scheduler]);

  const { model: presented, lag, canAct: schedulerCanAct } = useSyncExternalStore(
    scheduler.subscribe, scheduler.getSnapshot, scheduler.getSnapshot,
  );

  const actionState = resolveActionState(
    status,
    schedulerCanAct,
    isOnTurn(presented),
    isOnTurn(scheduler.getAuthoritative()),
  );
  const lifecycleBlocked = room?.lifecycle === "PAUSED" || room?.lifecycle === "ABORTED" || room?.lifecycle === "WAITING_FOR_HUMAN";
  const canAct = actionState === "ready" && !lifecycleBlocked && !(import.meta.env.DEV && roomId === "presentation-qa");

  const act = useCallback((action: WireAction) => {
    const socket = socketRef.current;
    if (socket === null || status !== "open" || !scheduler.canAct || lifecycleBlocked) return;
    const model = scheduler.getPresented();
    setRejection(null);
    socket.sendIntent(action, model.seq, model.turnToken);
  }, [scheduler, status, lifecycleBlocked]);

  const requestHint = useCallback(() => {
    const socket = socketRef.current;
    if (socket === null || status !== "open" || !scheduler.canAct || hintPending || lifecycleBlocked) return;
    const model = scheduler.getPresented();
    setHintPending(true);
    setHints([]);
    socket.sendHint(model.seq, model.turnToken);
  }, [hintPending, scheduler, status, lifecycleBlocked]);

  const clearHint = useCallback(() => {
    setHints([]);
    setHintPending(false);
  }, []);

  const sendReaction = useCallback((reactionId: ReactionId) => {
    socketRef.current?.sendReaction(reactionId);
  }, []);

  useEffect(() => {
    clearHint();
  }, [roomId, presented.turnToken, presented.handNumber, presented.hand, clearHint]);

  const serverNow = useCallback(() => socketRef.current?.serverNow() ?? Date.now(), []);
  const dismissRejection = useCallback(() => setRejection(null), []);

  return {
    presented, handSettlements, room, status, canAct, actionState, lag, rejection, hints, hintPending,
    serverNow, act, requestHint, clearHint, sendReaction, dismissRejection,
  };
}
